import Vue from "vue";
import { verifyGuestOtp, resendGuestOtp } from "@/utils/guestJoin/guestJoinApi";
import { trackEvent } from "@/utils/analytics/analytics";
import { GUEST_JOIN_OTP_SUBMITTED, GUEST_JOIN_OTP_LOCKED, } from "@/utils/analyticsEvents";
const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
export default Vue.extend({
    name: "OtpChallenge",
    props: {
        challengeToken: { type: String, required: true },
        phoneLast4: { type: String, default: "" },
    },
    data() {
        return {
            digits: new Array(CODE_LENGTH).fill(""),
            submitting: false,
            locked: false,
            verified: false,
            errorMessage: "",
            resendSecondsLeft: RESEND_COOLDOWN_SECONDS,
            resendIntervalId: null,
        };
    },
    computed: {
        code() {
            return this.digits.join("");
        },
        isComplete() {
            return this.code.length === CODE_LENGTH;
        },
    },
    mounted() {
        this.startResendCooldown();
        this.focusBox(0);
    },
    beforeDestroy() {
        if (this.resendIntervalId)
            clearInterval(this.resendIntervalId);
    },
    methods: {
        focusBox(index) {
            const refs = this.$refs[`digit-${index}`];
            refs?.[0]?.focus();
        },
        startResendCooldown() {
            this.resendSecondsLeft = RESEND_COOLDOWN_SECONDS;
            if (this.resendIntervalId)
                clearInterval(this.resendIntervalId);
            this.resendIntervalId = setInterval(() => {
                this.resendSecondsLeft = Math.max(0, this.resendSecondsLeft - 1);
                if (this.resendSecondsLeft === 0 && this.resendIntervalId) {
                    clearInterval(this.resendIntervalId);
                    this.resendIntervalId = null;
                }
            }, 1000);
        },
        onDigitInput(index, event) {
            const target = event.target;
            const raw = target.value.replace(/\D/g, "");
            if (raw.length > 1) {
                // SMS autofill dropped the whole code into one box — spread it across
                // the remaining boxes starting here, same as a paste.
                this.fillFrom(index, raw);
                return;
            }
            this.digits.splice(index, 1, raw);
            if (raw && index < CODE_LENGTH - 1) {
                this.focusBox(index + 1);
            }
            if (this.isComplete) {
                this.submit();
            }
        },
        onKeydown(index, event) {
            if (event.key === "Backspace" && !this.digits[index] && index > 0) {
                this.focusBox(index - 1);
            }
        },
        onPaste(event) {
            const pasted = event.clipboardData?.getData("text") ?? "";
            if (!pasted)
                return;
            event.preventDefault();
            this.fillFrom(0, pasted.replace(/\D/g, ""));
        },
        fillFrom(startIndex, value) {
            const chars = value.split("").slice(0, CODE_LENGTH - startIndex);
            chars.forEach((char, offset) => {
                this.digits.splice(startIndex + offset, 1, char);
            });
            const nextIndex = Math.min(startIndex + chars.length, CODE_LENGTH - 1);
            this.focusBox(nextIndex);
            if (this.isComplete) {
                this.submit();
            }
        },
        resetDigits() {
            this.digits = new Array(CODE_LENGTH).fill("");
            this.focusBox(0);
        },
        async resend() {
            this.errorMessage = "";
            try {
                const response = await resendGuestOtp(this.challengeToken);
                if (response.success) {
                    this.startResendCooldown();
                }
                else {
                    this.errorMessage =
                        response.err ||
                            "We couldn't resend the code just now. Please try again shortly.";
                }
            }
            catch {
                this.errorMessage =
                    "We couldn't resend the code just now. Please try again shortly.";
            }
        },
        async submit() {
            // submit() is reached from three places — the last digit landing, a
            // paste/SMS-autofill filling every box, and the button — and autofill
            // can fire more than one input event. Without the verified latch a
            // second one re-submits a code the backend has already consumed, which
            // comes back as "already used".
            if (this.submitting || this.locked || this.verified)
                return;
            this.submitting = true;
            this.errorMessage = "";
            try {
                const response = await verifyGuestOtp(this.challengeToken, this.code);
                this.submitting = false;
                // access_token is usually null here even on success — the clinician
                // isn't in the room yet — so success is judged on guest_session_token
                // alone; GuestVideoRoom.vue's tokenProvider fetches the real join
                // token afterwards via /token.
                if (response.success && response.guest_session_token) {
                    this.verified = true;
                    trackEvent(GUEST_JOIN_OTP_SUBMITTED, { success: "Yes" });
                    this.$emit("verified", {
                        guestSessionToken: response.guest_session_token,
                    });
                    return;
                }
                if (response.locked) {
                    trackEvent(GUEST_JOIN_OTP_SUBMITTED, {
                        success: "No",
                        outcome: "Locked",
                    });
                    trackEvent(GUEST_JOIN_OTP_LOCKED);
                    this.locked = true;
                    this.errorMessage = response.err || "";
                    this.$emit("locked");
                    return;
                }
                trackEvent(GUEST_JOIN_OTP_SUBMITTED, {
                    success: "No",
                    outcome: "Incorrect",
                });
                this.resetDigits();
                this.errorMessage =
                    response.err || "That code wasn't right. Please try again.";
            }
            catch {
                this.submitting = false;
                trackEvent(GUEST_JOIN_OTP_SUBMITTED, {
                    success: "No",
                    outcome: "Network Error",
                });
                this.errorMessage =
                    "We couldn't check your code — please check your connection and try again.";
            }
        },
    },
});
